import {
  CustomersResponse,
  UnifiedSearchResponse,
} from "@/core/customers/entities/Customers";
import {
  ApiReferentielCustomerItem,
  CustomersListMapper,
} from "@/core/customers/mappers/CustomersListMapper";
import {
  CoreBankingCustomersMapper,
  ApiCoreBankingCustomerItem,
} from "@/core/customers/mappers/CoreBankingCustomersMapper";
import CustomersRepository, {
  GetCustomersFilters,
  UnifiedSearchFilters,
} from "@/core/customers/repositories/CustomersRepository";
import {
  getCoreBankingImportUrl,
  getCoreBankingSearchUrl,
  getCustomersUrl,
  getUnifiedSearchUrl,
} from "../../api/customersBackendEndpoints/customersBackendEndpoints";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";
import { HttpException } from "http-repository-cache";

type GetCustomersRequest = GetCustomersFilters;

interface ApiCustomersSection<TItem> {
  items?: TItem[];
  totalElements?: number;
}

interface ApiUnifiedSearchPayload {
  customersRefId?: ApiCustomersSection<ApiReferentielCustomerItem>;
  customersCbs?: ApiCustomersSection<ApiCoreBankingCustomerItem>;
  totalElements?: number;
}

// src/infrastructure/customers/CustomersRepositoryBackend.ts
export default class CustomersRepositoryBackend implements CustomersRepository {
  constructor(private readonly httpClient: HttpClient) { }

  getCustomers({
    name,
    segment,
    agency,
    riskLevel,
    kyc,
    rm,
    page,
    size,
  }: GetCustomersFilters): Promise<CustomersResponse> {
    return this.httpClient
      .post<ApiResponse<CustomersResponse["data"]>, GetCustomersRequest>({
        url: getCustomersUrl(),
        body: { name, segment, agency, riskLevel, kyc, rm, page, size },
        withAuth: true,
      })
      .then((response) => {
        const result = ApiResponseHandler.handleResponse(response);
        if (!result.success) {
          throw ApiResponseHandler.createExceptionFromResponse(response);
        }

        const mappedContent = CustomersListMapper.toDomainItems({
          code: response.code,
          message: response.message,
          data: response.data,
        } as CustomersResponse);
        return {
          code: response.code,
          message: response.message,
          data: {
            ...response.data,
            items: mappedContent,
          },
        } as CustomersResponse;
      });
  }

  searchCoreBanking(accountNumber: string): Promise<CustomersResponse> {
    const sanitizedAccountNumber = accountNumber.trim();
    const endpoint = getCoreBankingSearchUrl();

    if (process.env.NODE_ENV !== "production") {
      console.debug(
        `[CustomersRepositoryBackend] Using fake response for ${endpoint} with account ${sanitizedAccountNumber}`
      );
    }

    // NOSONAR S1135 — no real core-banking search endpoint exists yet (nothing in
    // docs/openapi.json); this deliberately ships a fake response instead of an
    // executeRequest call. Migrating requires a real backend contract AND changing
    // CustomersRepository's searchCoreBanking signature to RepositoryResult, which
    // cascades into SearchCoreBankingCustomersUseCase, CustomersViewModel, and
    // customerStore.searchCustomersCoreBanking — tracked as follow-up work once the
    // endpoint exists, not a forgotten task.
    const fakeApiResponse: ApiResponse<CustomersResponse["data"]> = {
      code: "00",
      message: "Core banking search succeeded",
      data: {
        totalPages: 1,
        totalElements: sanitizedAccountNumber ? 1 : 0,
        pageable: {
          pageNumber: 0,
          pageSize: 10,
          sort: [],
          offset: 0,
          paged: true,
          unpaged: false,
        },
        size: 10,
        items: sanitizedAccountNumber
          ? ([
            {
              id: `CORE-${sanitizedAccountNumber}`,
              businessKey: `CORE-${sanitizedAccountNumber}`,
              full_name: "Client Core Banking",
              type: "PERSONNE_PHYSIQUE",
              branch: "Core Banking Branch",
              advisorName: "Advisor Core",
              kyc: "COMPLETE",
              riskLevel: "STANDARD",
              nbr_compte: sanitizedAccountNumber || "0",
              total_balance: "1 000 000 XOF",
              maj: new Date().toISOString().split("T")[0],
              imported: false,
            },
          ] as unknown as CustomersResponse["data"]["items"])
          : ([] as unknown as CustomersResponse["data"]["items"]),
        number: 0,
        sort: [],
        first: true,
        last: true,
        numberOfElements: sanitizedAccountNumber ? 1 : 0,
        empty: !sanitizedAccountNumber,
      },
    };

    const mappedItems = CustomersListMapper.toDomainItems(
      {
        code: fakeApiResponse.code,
        message: fakeApiResponse.message,
        data: fakeApiResponse.data,
      } as CustomersResponse,
      false
    );

    return Promise.resolve({
      code: fakeApiResponse.code,
      message: fakeApiResponse.message,
      data: {
        ...fakeApiResponse.data,
        items: mappedItems,
      },
    });
  }

  async searchUnified(
    filters: UnifiedSearchFilters
  ): Promise<UnifiedSearchResponse> {
    const endpoint = getUnifiedSearchUrl();
    const queryParams = this.buildQueryParams(filters);
    const queryString = this.buildQueryString(queryParams);
    const urlWithParams = queryString ? `${endpoint}?${queryString}` : endpoint;

    try {
      const response = await this.httpClient.get<
        ApiResponse<ApiUnifiedSearchPayload>
      >({
        url: urlWithParams,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse(response);
      if (!result.success) {
        throw ApiResponseHandler.createExceptionFromResponse(response);
      }

      const referentielSection =
        response.data?.customersRefId ??
        ({} as ApiCustomersSection<ApiReferentielCustomerItem>);
      const coreBankingSection =
        response.data?.customersCbs ??
        ({} as ApiCustomersSection<ApiCoreBankingCustomerItem>);

      const referentielClients = CustomersListMapper.toDomainItems({
        code: response.code,
        message: response.message,
        data: this.toCustomersResponseData(
          referentielSection.items ?? [],
          referentielSection.totalElements ?? 0,
          queryParams.rows ?? 10,
          queryParams.offset ?? 0
        ),
      } as CustomersResponse);

      const coreBankingClients = CoreBankingCustomersMapper.toDomainItems(
        (coreBankingSection.items as ApiCoreBankingCustomerItem[]) ?? []
      );

      const referentielTotal =
        referentielSection.totalElements ?? referentielClients.length;
      const coreBankingTotal =
        coreBankingSection.totalElements ?? coreBankingClients.length;
      const totalElements = response.data?.totalElements
        ? Number(response.data?.totalElements)
        : referentielTotal + coreBankingTotal;

      const rows = queryParams.rows ?? 10;
      const offset = queryParams.offset ?? 0;
      const pageNumber = rows > 0 ? Math.floor(offset / rows) : 0;
      const totalPages =
        rows > 0 ? Math.max(1, Math.ceil(totalElements / rows)) : 1;

      return {
        code: response.code,
        message: response.message,
        data: {
          coreBankingClients,
          referentielClients,
          totalPages,
          totalElements,
          pageable: {
            pageNumber,
            pageSize: rows,
            sort: [],
            offset,
            paged: true,
            unpaged: false,
          },
          size: rows,
          number: pageNumber,
          sort: [],
          first: pageNumber === 0,
          last: pageNumber + 1 >= totalPages,
          numberOfElements:
            referentielClients.length + coreBankingClients.length,
          empty:
            referentielClients.length === 0 && coreBankingClients.length === 0,
        },
      };
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.debug(
          `[CustomersRepositoryBackend] Failed unified search for ${urlWithParams}`,
          error
        );
      }

      const rows = queryParams.rows ?? 10;
      const offset = queryParams.offset ?? 0;

      return {
        code: "99",
        message: "Unified search failed",
        data: {
          coreBankingClients: [],
          referentielClients: [],
          totalPages: 1,
          totalElements: 0,
          pageable: {
            pageNumber: rows > 0 ? Math.floor(offset / rows) : 0,
            pageSize: rows,
            sort: [],
            offset,
            paged: true,
            unpaged: false,
          },
          size: rows,
          number: rows > 0 ? Math.floor(offset / rows) : 0,
          sort: [],
          first: true,
          last: true,
          numberOfElements: 0,
          empty: true,
        },
      };
    }
  }

  private buildQueryParams(filters: UnifiedSearchFilters) {
    const rows = filters.rows ?? filters.size ?? 10;
    const offset = filters.offset ?? (filters.page ?? 0);

    return {
      filterValue: filters.filterValue ?? filters.name,
      accountNumber: filters.accountNumber,
      customerType: filters.customerType ?? filters.segment,
      offset,
      rows,
      sensitive: filters.sensitive ?? undefined,
      // Bornes de periode omises quand elles sont vides : buildQueryString ecarte les valeurs
      // indefinies, le back ne recoit donc que les bornes reellement posees.
      createdFrom: filters.createdFrom || undefined,
      createdTo: filters.createdTo || undefined,
    };
  }

  private buildQueryString(
    params: Record<string, string | number | boolean | undefined>
  ) {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      searchParams.set(key, String(value));
    });

    return searchParams.toString();
  }

  private toCustomersResponseData(
    items: ApiReferentielCustomerItem[],
    totalElements: number,
    size: number,
    offset: number
  ): CustomersResponse["data"] {
    const pageNumber = size > 0 ? Math.floor(offset / size) : 0;
    const totalPages =
      size > 0 ? Math.max(1, Math.ceil(totalElements / size)) : 1;
    return {
      totalPages,
      totalElements,
      pageable: {
        pageNumber,
        pageSize: size,
        sort: [],
        offset,
        paged: true,
        unpaged: false,
      },
      size,
      items: items as unknown as CustomersResponse["data"]["items"],
      number: pageNumber,
      sort: [],
      first: pageNumber === 0,
      last: pageNumber + 1 >= totalPages,
      numberOfElements: items.length,
      empty: items.length === 0,
    };
  }

  async importCoreBankingCustomer(
    customer: unknown
  ): Promise<string | undefined> {
    const sanitizedAccountNumber = customer && (customer as unknown);
    const endpoint = getCoreBankingImportUrl();

    try {
      const response = await this.httpClient.post<
        ApiResponse<string>,
        unknown
      >({
        url: endpoint,
        body: sanitizedAccountNumber,
        withAuth: true,
      });

      // Handle the standardized API response format
      const result = ApiResponseHandler.handleResponse(response);
      if (!result.success) {
        throw ApiResponseHandler.createExceptionFromResponse(response);
      }

      return (
        response.data ?? {
          id: `CLI-${sanitizedAccountNumber}`,
          businessKey: `CLI-${sanitizedAccountNumber}`,
        }
      );
    } catch (error) {
      // Check if this is an HTTP error (like 500) or API error response
      // If it's an ApiResponse with error code, handle it
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        "message" in error &&
        "data" in error
      ) {
        const apiError = error as ApiResponse<unknown>;
        const result = ApiResponseHandler.handleResponse(apiError);
        if (!result.success) {
          throw ApiResponseHandler.createExceptionFromResponse(apiError);
        }
      }

      // Handle HTTP exceptions (like 500) by mapping them to exceptions
      throw handleRequestError(
        error as HttpException<unknown, unknown> | ApiResponse<unknown>
      );
    }
  }
}
